// src/App.js
// Проверка IP: 94.140.115.156, 88.99.149.145
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  Container,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
} from '@mui/material';
import { DataGrid } from 'react-data-grid';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-data-grid/lib/styles.css';

const API_BASE = '';

// ---------- IP-Guard ----------
const ALLOWED_IPS = ['94.140.115.156', '88.99.149.145', '87.251.78.128', '216.185.57.207', '94.233.100.67', '212.46.38.149'];
function useIPGuard() {
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('https://api.ipify.org?format=json');
        if (!ALLOWED_IPS.includes(data.ip)) {
          window.location = 'about:blank';
          return;
        }
        setAuthorized(true);
      } catch {
        window.location = 'about:blank';
      }
    })();
  }, []);

  return authorized;
}
// ------------------------------

function App() {
  const isAuthorized = useIPGuard();
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState({ data: [], merges: [] });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!isAuthorized) return;
    fetchReports();
  }, [isAuthorized]);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API_BASE}/reports`);
      setReports(res.data);
      const today = dayjs().format('YYYY-MM-DD');
      const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      const defaultReport =
        res.data.find((r) => r.date === today) ||
        res.data.find((r) => r.date === yesterday) ||
        res.data[0];
      if (defaultReport) {
        setSelectedReport(defaultReport.filename);
        fetchReportData(defaultReport.filename);
      }
    } catch (err) {
      console.error('Fetch reports error:', err);
      toast.error('Ошибка загрузки списка отчётов');
    }
  };

  const fetchReportData = async (filename) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/report/${filename}`);
      const data = res.data.data;
      const maxCols = data.reduce((max, row) => Math.max(max, row.length), 0);
      data.forEach((row) => {
        while (row.length < maxCols) {
          row.push({ value: '', type: 'z', style: null });
        }
      });
      setReportData(res.data);
    } catch (err) {
      console.error('Fetch report error:', err);
      toast.error('Ошибка загрузки отчёта');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API_BASE}/generate`);
      toast.success('Отчёт сформирован и отправлен на почту');
      fetchReports();
    } catch (err) {
      console.error('Generate error:', err);
      toast.error('Ошибка при формировании отчёта');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (selectedReport) {
      window.location.href = `${API_BASE}/download/${selectedReport}`;
    }
  };

  const columns = useMemo(() => {
    return (
      reportData.data[0]?.map((cell, idx) => ({
        key: `col${idx}`,
        name: cell?.value || `Column ${idx + 1}`,
        resizable: true,
        width: 150,
        formatter: ({ row }) => {
          const cellData = row[`col${idx}`];
          const style = {};
          if (
            cellData?.style?.fill?.fgColor?.rgb &&
            typeof cellData.style.fill.fgColor.rgb === 'string'
          ) {
            const rgb = cellData.style.fill.fgColor.rgb;
            style.backgroundColor = `#${
              rgb.length === 8 ? rgb.slice(2) : rgb
            }`;
          }
          return (
            <div style={{ ...style, whiteSpace: 'pre-wrap' }}>
              {cellData?.value ?? ''}
            </div>
          );
        },
      })) || []
    );
  }, [reportData]);

  const rows = useMemo(() => {
    return reportData.data.slice(1).map((rowData, rowIdx) => {
      const row = { id: rowIdx };
      rowData.forEach((cell, colIdx) => {
        row[`col${colIdx}`] = cell;
      });
      return row;
    });
  }, [reportData]);

  if (!isAuthorized) return <CircularProgress />;

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Просмотр отчётов по ДЗ
      </Typography>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Выберите отчёт</InputLabel>
              <Select
                value={selectedReport || ''}
                onChange={(e) => {
                  setSelectedReport(e.target.value);
                  fetchReportData(e.target.value);
                }}
              >
                {reports.map((report) => (
                  <MenuItem key={report.filename} value={report.filename}>
                    {report.date}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerate}
              disabled={generating}
              sx={{ mr: 2 }}
            >
              {generating ? 'Формирую...' : 'Сформировать новый и отправить на почту'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleDownload}
              disabled={!selectedReport}
            >
              Скачать
            </Button>
          </Grid>
        </Grid>
        {loading ? (
          <CircularProgress />
        ) : (
          <div
            style={{
              height: 'calc(80vh - 100px)',
              width: '100%',
              overflow: 'auto',
            }}
          >
            <DataGrid columns={columns} rows={rows} style={{ height: '100%' }} />
          </div>
        )}
      </Paper>
      <ToastContainer position="top-right" autoClose={5000} />
    </Container>
  );
}

export default App;